import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Edit, Ruler, Save, Search, X } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Badge, Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableCell, TableHeader, TableRow } from '@components/Table';
import { usePermissions } from '@hooks/usePermissions';
import { productService } from '@services/productService';
import type { ProductDimensionItem, ProductNameGroup } from '@/types/product';
import {
  buildDimensionsPayload,
  buildDraft,
  computeDimensionDivergence,
  formatDimension,
  formatWeight,
  type DimensionDraft,
} from './productDimensionsUtils';

type StatusFilter = 'all' | 'active' | 'inactive';

const GROUPS_PER_PAGE = 20;

/**
 * Sub-aba "Nomes e Dimensões" (Produtos).
 *
 * Existe para diagnosticar produtos cadastrados com o MESMO nome e medidas
 * diferentes. A listagem principal não serve para isso: ela ordena antes por
 * "tem estoque disponível", então as duplicatas ficam a páginas de distância.
 *
 * O filtro de status abre em "Todos" de propósito — a maior parte dos nomes
 * duplicados está em produto inativo, e esconder os inativos esconderia
 * justamente o que a tela existe para mostrar.
 */
export const ProductNameDimensionsView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canEdit = checkPermission('products.write').hasPermission;

  const [groups, setGroups] = useState<ProductNameGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [duplicatesOnly, setDuplicatesOnly] = useState(true);
  const [status, setStatus] = useState<StatusFilter>('all');

  const [page, setPage] = useState(1);
  const [totalGroups, setTotalGroups] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<DimensionDraft | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});

  // Debounce da busca no mesmo formato da listagem de produtos (500 ms).
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, duplicatesOnly, status]);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { groups: rows, pagination } = await productService.getProductNameGroups({
        page,
        limit: GROUPS_PER_PAGE,
        q: debouncedSearch || undefined,
        duplicatesOnly,
        isActive: status === 'all' ? undefined : status === 'active',
      });

      setGroups(rows);
      setTotalGroups(pagination?.total_items ?? rows.length);
      setTotalPages(pagination?.total_pages ?? 1);
    } catch (err) {
      setGroups([]);
      setTotalGroups(0);
      setTotalPages(0);
      setError(err instanceof Error ? err.message : 'Erro ao carregar os produtos');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, duplicatesOnly, status]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // A divergência é recalculada sobre os dados em memória: assim o destaque
  // some no instante em que as medidas passam a bater, sem recarregar a lista.
  const divergenceByGroup = useMemo(
    () => groups.map((group) => computeDimensionDivergence(group.items)),
    [groups]
  );

  const startEdit = (item: ProductDimensionItem) => {
    setEditingId(item.id);
    setDraft(buildDraft(item));
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[item.id];
      return next;
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
  };

  const save = async (item: ProductDimensionItem) => {
    if (!draft) return;

    const payload = buildDimensionsPayload(draft, item);

    if (payload === undefined) {
      setRowErrors((prev) => ({
        ...prev,
        [item.id]: 'Medidas em centímetros inteiros; peso com até 4 casas decimais.',
      }));
      return;
    }

    // Nada mudou: não gasta uma gravação que só carimbaria o "atualizado por".
    if (payload === null) {
      cancelEdit();
      return;
    }

    setSavingId(item.id);
    try {
      const updated = await productService.updateProductDimensions(item.id, payload);

      setGroups((prev) =>
        prev.map((group) => ({
          ...group,
          items: group.items.map((row) =>
            row.id === item.id
              ? {
                  ...row,
                  width: updated.width ?? null,
                  height: updated.height ?? null,
                  length: updated.length ?? null,
                  total_weight: updated.total_weight ?? null,
                  updated_at: updated.updated_at ?? row.updated_at,
                  last_updated_by: updated.last_updated_by ?? row.last_updated_by,
                }
              : row
          ),
        }))
      );

      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      cancelEdit();
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [item.id]: err instanceof Error ? err.message : 'Falha ao salvar as dimensões',
      }));
    } finally {
      setSavingId(null);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent, item: ProductDimensionItem) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      save(item);
    } else if (event.key === 'Escape') {
      cancelEdit();
    }
  };

  const setDraftField = (field: keyof DimensionDraft, value: string) =>
    setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));

  const cellClass = (diverges: boolean) =>
    diverges ? 'text-right font-semibold text-amber-700 bg-amber-50' : 'text-right';

  const renderMeasureInput = (field: keyof DimensionDraft, item: ProductDimensionItem) => (
    <input
      type="text"
      inputMode="decimal"
      className="w-20 border border-gray-300 rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-emerald-500"
      value={draft?.[field] ?? ''}
      onChange={(e) => setDraftField(field, e.target.value)}
      onKeyDown={(e) => handleKeyPress(e, item)}
      disabled={savingId === item.id}
    />
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Nomes e Dimensões</h1>
        <p className="text-sm text-gray-500 mt-1">
          Produtos agrupados pelo nome (ignorando caixa, acentos e espaços) para comparar e corrigir
          as medidas. Inclui produtos inativos.
        </p>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar por nome ou SKU..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit">
              {(
                [
                  { id: true, label: 'Só nomes duplicados' },
                  { id: false, label: 'Todos os nomes' },
                ] as const
              ).map((option) => (
                <button
                  key={String(option.id)}
                  type="button"
                  onClick={() => setDuplicatesOnly(option.id)}
                  className={
                    duplicatesOnly === option.id
                      ? 'px-4 py-1.5 text-sm font-medium rounded-md bg-white text-emerald-700 shadow-sm'
                      : 'px-4 py-1.5 text-sm font-medium rounded-md text-slate-500 hover:text-slate-700'
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>

            <select
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
            >
              <option value="all">Ativos e inativos</option>
              <option value="active">Somente ativos</option>
              <option value="inactive">Somente inativos</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {error && (
            <div className="p-4 text-sm text-red-600 border-b border-red-100 bg-red-50">{error}</div>
          )}

          {loading ? (
            <div className="p-8 text-center text-gray-500">Carregando...</div>
          ) : groups.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Ruler size={40} className="mx-auto mb-3 text-gray-300" />
              {duplicatesOnly
                ? 'Nenhum nome duplicado com os filtros atuais.'
                : 'Nenhum produto encontrado.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell className="font-semibold">SKU</TableCell>
                  <TableCell className="font-semibold">Nome cadastrado</TableCell>
                  <TableCell className="font-semibold">Status</TableCell>
                  <TableCell className="font-semibold text-right">Largura</TableCell>
                  <TableCell className="font-semibold text-right">Altura</TableCell>
                  <TableCell className="font-semibold text-right">Compr.</TableCell>
                  <TableCell className="font-semibold text-right">Peso (kg)</TableCell>
                  <TableCell className="font-semibold">Un.</TableCell>
                  <TableCell className="font-semibold text-right">Ações</TableCell>
                </TableRow>
              </TableHeader>
              <tbody>
                {groups.map((group, groupIndex) => {
                  const divergence = divergenceByGroup[groupIndex];

                  return (
                    <React.Fragment key={group.name_key || `grupo-${groupIndex}`}>
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={9}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900">
                              {group.display_name || '(sem nome)'}
                            </span>
                            {group.item_count > 1 && (
                              <Badge variant="neutral" size="sm">
                                {group.item_count} produtos
                              </Badge>
                            )}
                            {divergence.any && (
                              <Badge variant="warning" size="sm" className="gap-1">
                                <AlertTriangle size={12} />
                                Medidas divergentes
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>

                      {group.items.map((item) => {
                        const isEditing = editingId === item.id;
                        const rowError = rowErrors[item.id];

                        return (
                          <TableRow key={item.id}>
                            <TableCell className="font-mono text-sm">{item.sku}</TableCell>
                            <TableCell>
                              <span className="text-sm text-gray-700">{item.description || '—'}</span>
                              {rowError && (
                                <div className="text-xs text-red-600 mt-1">{rowError}</div>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant={item.is_active ? 'success' : 'neutral'} size="sm">
                                {item.is_active ? 'Ativo' : 'Inativo'}
                              </Badge>
                            </TableCell>

                            <TableCell className={cellClass(divergence.width)}>
                              {isEditing ? renderMeasureInput('width', item) : formatDimension(item.width)}
                            </TableCell>
                            <TableCell className={cellClass(divergence.height)}>
                              {isEditing ? renderMeasureInput('height', item) : formatDimension(item.height)}
                            </TableCell>
                            <TableCell className={cellClass(divergence.length)}>
                              {isEditing ? renderMeasureInput('length', item) : formatDimension(item.length)}
                            </TableCell>
                            <TableCell className={cellClass(divergence.total_weight)}>
                              {isEditing
                                ? renderMeasureInput('total_weight', item)
                                : formatWeight(item.total_weight)}
                            </TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {item.measure || 'cm'}
                            </TableCell>

                            <TableCell className="text-right">
                              {canEdit && (
                                <div className="flex items-center justify-end gap-1">
                                  {isEditing ? (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => save(item)}
                                        disabled={savingId === item.id}
                                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded disabled:opacity-50"
                                        title="Salvar (Enter)"
                                      >
                                        <Save size={14} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={cancelEdit}
                                        disabled={savingId === item.id}
                                        className="p-1.5 text-slate-500 hover:bg-slate-100 rounded disabled:opacity-50"
                                        title="Cancelar (Esc)"
                                      >
                                        <X size={14} />
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => startEdit(item)}
                                      className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded"
                                      title="Editar dimensões"
                                    >
                                      <Edit size={14} />
                                    </button>
                                  )}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <div className="text-sm text-gray-500">
                Mostrando {Math.min((page - 1) * GROUPS_PER_PAGE + 1, totalGroups)} a{' '}
                {Math.min(page * GROUPS_PER_PAGE, totalGroups)} de {totalGroups} nomes
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1 || loading}
                >
                  Anterior
                </Button>
                <span className="text-sm text-gray-500">
                  Página {page} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || loading}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductNameDimensionsView;
